import { AmbassadorBrand, AmbassadorResponse, BrandContentType } from 'services/api/ambassadors'
import { GridFilterModel, GridSortItem } from '@mui/x-data-grid-pro'
import { OptionsState } from 'particles/Dropdown'
import { PLATFORM_CONTENT_TYPE_ENUM } from 'services/api/constants'
import { createSelector } from 'reselect'
import { FIELDS_TYPE, getSortQueryText } from 'components/utils/filterAndSort'
import { State } from 'state'

// Actions
export enum AmbassadorActions {
    UPDATE_AMBASSADOR_FAVORITE = 'ambassadors/UPDATE_AMBASSADOR_FAVORITE',
    UPDATE_AMBASSADOR_FAVORITE_SUCCESS = 'ambassadors/UPDATE_AMBASSADOR_FAVORITE_SUCCESS',
    GET_AMBASSADOR_BRANDS = 'ambassadors/GET_AMBASSADOR_BRANDS',
    SAVE_AMBASSADOR_BRANDS = 'ambassadors/SAVE_AMBASSADOR_BRANDS',
    GET_BRAND_CONTENT_TYPES = 'ambassadors/GET_BRAND_CONTENT_TYPES',
    SAVE_BRAND_CONTENT_TYPES = 'ambassadors/SAVE_BRAND_CONTENT_TYPES',
    GET_AMBASSADOR_FILTERS = 'ambassadors/GET_AMBASSADOR_FILTERS',
    GET_AMBASSADOR_LIST = 'ambassadors/GET_AMBASSADOR_LIST',
    CANCEL_GET_AMBASSADOR_LIST = 'ambassadors/CANCEL_GET_AMBASSADOR_LIST',
    SAVE_AMBASSADOR_LIST = 'ambassadors/SAVE_AMBASSADOR_LIST',
    SET_FILTER_MODEL = 'ambassadors/SET_FILTER_MODEL',
    SET_FILTER_OPTIONS = 'ambassadors/SET_FILTER_OPTIONS',
    SET_SORT_ITEM = 'ambassadors/SET_SORT_ITEM',
    SET_SEARCH_TERM = 'ambassadors/SET_SEARCH_TERM',
}

export interface GetAmbassadorListPayload {
    brands: string[]
    content_type: PLATFORM_CONTENT_TYPE_ENUM
    ordering?: string
    offset?: number
    filteringParams?: {
        [key: string]: string[] | string | undefined
    }
}

export const getAmbassadorList = (payload: GetAmbassadorListPayload) => ({
    type: AmbassadorActions.GET_AMBASSADOR_LIST,
    payload,
})

export const cancelGetAmbassadorList = () => ({
    type: AmbassadorActions.CANCEL_GET_AMBASSADOR_LIST,
})

export const saveAmbassadorList = (data: AmbassadorResponse[]) => ({
    type: AmbassadorActions.SAVE_AMBASSADOR_LIST,
    payload: data,
})

export interface GetAmbassadorFiltersPayload {
    brands: string[]
    content_type: PLATFORM_CONTENT_TYPE_ENUM
}

export const getAmbassadorFilters = (payload: GetAmbassadorFiltersPayload) => ({
    type: AmbassadorActions.GET_AMBASSADOR_FILTERS,
    payload,
})

export interface GetBrandContentTypesPayload {
    brands: string[]
}

export const getBrandContentTypes = (payload: GetBrandContentTypesPayload) => ({
    type: AmbassadorActions.GET_BRAND_CONTENT_TYPES,
    payload,
})

export const saveBrandContentTypes = (data: BrandContentType[]) => ({
    type: AmbassadorActions.SAVE_BRAND_CONTENT_TYPES,
    payload: data,
})

export interface GetAmbassadorBrandsPayload {
    name?: string
    team_uuid?: string
}

export const getAmbassadorBrands = (payload: GetAmbassadorBrandsPayload) => ({
    type: AmbassadorActions.GET_AMBASSADOR_BRANDS,
    payload,
})

export const saveAmbassadorBrands = (data: AmbassadorBrand[]) => ({
    type: AmbassadorActions.SAVE_AMBASSADOR_BRANDS,
    payload: data,
})

export interface UpdateAmbassadorFavoritePayload {
    partiposter_uuid: string
    brand_uuid: string
    brand_name: string
    favorited: boolean
}

export const updateAmbassadorFavorite = (payload: UpdateAmbassadorFavoritePayload) => ({
    type: AmbassadorActions.UPDATE_AMBASSADOR_FAVORITE,
    payload,
})

export const updateAmbassadorFavoriteSuccess = (payload: UpdateAmbassadorFavoritePayload) => ({
    type: AmbassadorActions.UPDATE_AMBASSADOR_FAVORITE_SUCCESS,
    payload,
})

interface SetFilterModelPayload {
    options: GridFilterModel | null
}

interface SetSortItemPayload {
    options:
        | (GridSortItem & {
              fieldLabel: string
              sortDirectionLabel: string
          })
        | null
}

export const setFilterModel = (payload: SetFilterModelPayload) => ({
    payload,
    type: AmbassadorActions.SET_FILTER_MODEL,
})

export const setFilterOptions = (payload: OptionsState[]) => ({
    payload,
    type: AmbassadorActions.SET_FILTER_OPTIONS,
})

export const setSortItem = (payload: SetSortItemPayload | null) => ({
    payload,
    type: AmbassadorActions.SET_SORT_ITEM,
})
export const setSearchTerm = (payload: string) => ({
    payload,
    type: AmbassadorActions.SET_SEARCH_TERM,
})

export interface AmbassadorState {
    favorites: Record<string, boolean>
    brands: AmbassadorBrand[]
    contentTypes: BrandContentType[]
    ambassadors: AmbassadorResponse[]
    filterModel: GridFilterModel
    filterOptions: OptionsState[]
    searchTerm: string
    sortItem:
        | (GridSortItem & {
              fieldLabel: string
              sortDirectionLabel: string
          })
        | null
}

const initialState: AmbassadorState = {
    favorites: {},
    brands: [],
    contentTypes: [],
    ambassadors: [],
    filterModel: {
        items: [],
    },
    searchTerm: '',
    filterOptions: [],
    sortItem: null,
}

export const ambassadorReducer = (state = initialState, action: any): AmbassadorState => {
    switch (action.type) {
        case AmbassadorActions.UPDATE_AMBASSADOR_FAVORITE_SUCCESS:
            return {
                ...state,
                favorites: {
                    ...state.favorites,
                    [action.payload.partiposter_uuid]: action.payload.favorited,
                },
            }
        case AmbassadorActions.SAVE_AMBASSADOR_BRANDS:
            return {
                ...state,
                brands: action.payload,
            }
        case AmbassadorActions.SAVE_BRAND_CONTENT_TYPES:
            return {
                ...state,
                contentTypes: action.payload,
            }

        case AmbassadorActions.SAVE_AMBASSADOR_LIST:
            return {
                ...state,
                ambassadors: action.payload,
            }
        case AmbassadorActions.SET_FILTER_MODEL: {
            const { options } = action.payload
            return {
                ...state,
                filterModel: options,
            }
        }

        case AmbassadorActions.SET_FILTER_OPTIONS: {
            return {
                ...state,
                filterOptions: action.payload,
            }
        }

        case AmbassadorActions.SET_SORT_ITEM: {
            const { options } = action.payload || {}
            return {
                ...state,
                sortItem: options,
            }
        }
        case AmbassadorActions.SET_SEARCH_TERM: {
            return {
                ...state,
                searchTerm: action.payload,
            }
        }
        default:
            return state
    }
}

export const selectAmbassadorList = (state: { ambassadors: AmbassadorState }) => state.ambassadors.ambassadors
export const selectAmbassadorBrands = (state: { ambassadors: AmbassadorState }) => state.ambassadors.brands
export const selectFilterOptions = (state: { ambassadors: AmbassadorState }) => state.ambassadors.filterOptions
export const selectFilterModel = (state: { ambassadors: AmbassadorState }) => state.ambassadors.filterModel
export const selectSortItem = (state: { ambassadors: AmbassadorState }) => state.ambassadors.sortItem
export const selectSearchTerm = (state: { ambassadors: AmbassadorState }) => state.ambassadors.searchTerm
export const selectAmbassadorFavorited = (state: { ambassadors: AmbassadorState }) => state.ambassadors.favorites

export const selectSelectedSort = createSelector([selectSortItem], (gridSortItem): string | null => {
    if (gridSortItem && gridSortItem.field) {
        return getSortQueryText(gridSortItem.field, gridSortItem.sort || 'desc')
    } else {
        return null
    }
})

interface ContentTypeReturn {
    instagram?: string[]
    facebook?: string[]
    tiktok?: string[]
    youtube?: string[]
    xiaohongshu?: string[]
}

export const getPlatformFromContentType = (type: PLATFORM_CONTENT_TYPE_ENUM): keyof ContentTypeReturn | null => {
    if (
        [
            PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_POST,
            PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_REEL,
            PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY,
        ].includes(type)
    ) {
        return 'instagram'
    }

    if (type === PLATFORM_CONTENT_TYPE_ENUM.FACEBOOK_POST) {
        return 'facebook'
    }

    if (type === PLATFORM_CONTENT_TYPE_ENUM.TIKTOK_POST) {
        return 'tiktok'
    }

    if (type === PLATFORM_CONTENT_TYPE_ENUM.YOUTUBE_VIDEO) {
        return 'youtube'
    }

    if (type === PLATFORM_CONTENT_TYPE_ENUM.XIAOHONGSHU_POST) {
        return 'xiaohongshu'
    }

    return null
}

export const selectBrandContentTypes = (state: State): ContentTypeReturn => {
    const responseContentTypes = state.ambassadors.contentTypes

    if (!responseContentTypes.length) return {}

    const contentTypeMap = {} as ContentTypeReturn

    responseContentTypes.forEach(types => {
        types.value?.forEach(value => {
            const platform = getPlatformFromContentType(value.platform_content_type)
            if (platform) {
                const currValue = contentTypeMap[platform] || []
                contentTypeMap[platform] = currValue.includes(value.platform_content_type)
                    ? currValue
                    : [...currValue, value.platform_content_type]
            }
        })
    })

    return contentTypeMap
}

export const selectCheckIsContentTypeExist = createSelector([selectBrandContentTypes], contentTypes => {
    return (contentType: PLATFORM_CONTENT_TYPE_ENUM) => {
        const platform = getPlatformFromContentType(contentType)
        if (!platform) return false
        return contentTypes[platform]?.includes(contentType)
    }
})

export const selectFilteringParams = createSelector([selectFilterModel], filterModel => {
    if (!filterModel) return {}

    const SUFFIX_BY_OPERATOR_MAP = {
        contains: 'contains',
        startsWith: 'startswith',
        endsWith: 'endswith',
        '=': 'eq',
        '!=': 'neq',
        '>': 'gt',
        '>=': 'gte',
        '<': 'lt',
        '<=': 'lte',
    }

    const { items } = filterModel as GridFilterModel
    if (!items) {
        return {}
    }
    return items.reduce((acc: { [key: string]: string | string[] | undefined }, curr) => {
        const columnField = curr.columnField
        if (!columnField || !curr.value) return acc

        const suffix = SUFFIX_BY_OPERATOR_MAP[curr.operatorValue as keyof typeof SUFFIX_BY_OPERATOR_MAP]

        if (columnField === FIELDS_TYPE.AGE) {
            if (curr.operatorValue === '=') return { ...acc, age: curr.value }
            return {
                ...acc,
                [`${FIELDS_TYPE.AGE}__${suffix}`]: curr.value,
            }
        } else if (
            columnField === FIELDS_TYPE.IG_FOLLOWERS ||
            columnField === FIELDS_TYPE.FB_FRIENDS ||
            columnField === FIELDS_TYPE.TIKTOK_FOLLOWERS ||
            columnField === FIELDS_TYPE.TWITTER_FOLLOWERS ||
            columnField === FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS
        ) {
            return {
                ...acc,
                social_media_follower_number_tier: acc.social_media_follower_number_tier
                    ? `${acc.social_media_follower_number_tier},${curr.value}`
                    : curr.value?.toString(),
            }
        } else if (columnField === FIELDS_TYPE.NAME) {
            if (!suffix) return { ...acc, name: curr.value }
            return {
                ...acc,
                [`${FIELDS_TYPE.NAME}__${suffix}`]: curr.value,
            }
        } else if (columnField === FIELDS_TYPE.STATUS) {
            return {
                ...acc,
                is_viewed: acc.is_viewed ? `${acc.is_viewed},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.PARTIPOSTER_TAGS) {
            return {
                ...acc,
                tags: acc.tags ? `${acc.tags},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.PROFILE_TYPE) {
            return {
                ...acc,
                profile_tier: acc.profile_tier ? `${acc.profile_tier},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.GENDER) {
            return {
                ...acc,
                gender: acc.gender ? `${acc.gender},${curr.value}` : curr.value,
            }
        }
        return acc
    }, {})
})
